import React, { useState, useEffect, useRef } from 'react';
import AppRoutes from './routes';
import './App.css';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BarChart3, Building2, Sparkles, ShieldCheck, UserPlus, Moon, Sun, LogOut, UserCircle, Lock } from 'lucide-react';
import ChakanaIcon from './components/ChakanaIcon';
import { useAuth } from './contexts/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import ChatDrawer from './components/ChatDrawer';

function App() {
  const location = useLocation();
  const onPropertyDetails = /^\/properties\/\d+$/.test(location.pathname);
  const { user, logout, loading, tier, hasFeature } = useAuth();
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const navigate = useNavigate();
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [apuOpen, setApuOpen] = useState(false);
  const avatarMenuRef = useRef(null);

  const [theme, setTheme] = useState(() => localStorage.getItem('ayni-theme') || 'light');

  // Listen for upgrade-required events from API interceptor
  useEffect(() => {
    const handler = (e) => {
      setUpgradeFeature(e.detail?.feature || '');
      setUpgradeModal(true);
    };
    window.addEventListener('ayni-upgrade-required', handler);
    return () => window.removeEventListener('ayni-upgrade-required', handler);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ayni-theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setAvatarMenuOpen(false);
      }
    };
    if (avatarMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [avatarMenuOpen]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  if (loading) {
    return <LoadingScreen message="Loading AYNI..." />;
  }

  // Login page: no sidebar
  if (location.pathname === '/login') {
    return (
      <div className="App" style={{ display: 'block' }}>
        <AppRoutes />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="sidebar">
        <div className="logo-container">
          <Link to="/">
            <img
              src="/assets/ayni_logo.png"
              alt="AYNI Logo"
              className="sidebar-logo"
            />
          </Link>
        </div>
        <nav>
          <ul>
            <li>
              <NavLink to="/" end className={({ isActive }) => `nav-link${isActive || onPropertyDetails ? ' active' : ''}`}>
                <LayoutDashboard size={18} /> <span>Cockpit</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard" className="nav-link">
                <BarChart3 size={18} /> <span>Dashboard</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/properties" className={({ isActive }) => `nav-link${isActive && !onPropertyDetails ? ' active' : ''}`}>
                <Building2 size={18} /> <span>Properties</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/cleaners" className="nav-link">
                <Sparkles size={18} /> <span>Aynistas</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admins" className="nav-link">
                <ShieldCheck size={18} /> <span>Admins</span>
              </NavLink>
            </li>
            {user?.role === 'superadmin' && (
              <li>
                <NavLink to="/access" className="nav-link">
                  <UserPlus size={18} /> <span>Access</span>
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
        <div style={{ padding: '0 10px 2px', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '0' }}>
          <button
            onClick={() => hasFeature('apu') ? setApuOpen(true) : setUpgradeModal(true)}
            className="apu-sidebar-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%', padding: '10px 12px', border: 'none',
              borderRadius: '6px', borderLeft: '3px solid transparent',
              backgroundColor: 'transparent',
              color: '#c9a86c', fontSize: '13px', fontWeight: 500,
              cursor: 'pointer', letterSpacing: '0.2px', boxShadow: 'none',
              margin: 0, marginTop: '8px',
              fontFamily: "'Inter', sans-serif",
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(201, 168, 108, 0.08)';
              e.currentTarget.style.color = '#dbb878';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#c9a86c';
            }}
          >
            <ChakanaIcon size={18} color="currentColor" />
            <span>APU</span>
            {hasFeature('apu') ? (
              <span style={{
                width: 5, height: 5, borderRadius: '50%', marginLeft: 'auto',
                backgroundColor: '#7bc47f',
                boxShadow: '0 0 6px rgba(123, 196, 127, 0.6)',
                animation: 'apuBreath 3s ease-in-out infinite',
              }} />
            ) : (
              <Lock size={12} style={{ marginLeft: 'auto', opacity: 0.4 }} />
            )}
          </button>
        </div>
        <div style={{ padding: '4px 10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={toggleTheme}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%', padding: '10px 12px', border: 'none',
              borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.65)', fontSize: '13px', fontWeight: '500',
              cursor: 'pointer', letterSpacing: '0.2px', boxShadow: 'none',
              margin: 0,
            }}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          </button>
        </div>
      </div>
      <div className="main-content">
        {/* Impersonation Banner */}
        {localStorage.getItem('ayni-superadmin-token') && (
          <div style={{
            backgroundColor: '#ef4444', color: '#fff', padding: '8px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: '13px', fontWeight: '600',
          }}>
            <span>You are impersonating {user?.name || 'a user'}. Changes will affect their account.</span>
            <button
              onClick={() => {
                const superToken = localStorage.getItem('ayni-superadmin-token');
                localStorage.setItem('ayni-access-token', superToken);
                localStorage.removeItem('ayni-superadmin-token');
                window.location.reload();
              }}
              style={{
                padding: '4px 16px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.4)',
                backgroundColor: 'transparent', color: '#fff', fontSize: '12px',
                fontWeight: '600', cursor: 'pointer',
              }}
            >
              Exit Impersonation
            </button>
          </div>
        )}

        {/* Top Bar */}
        <div className="topbar">
          <div className="topbar-actions">
            <button className="topbar-icon-btn" title="Notifications">
              <i className="fa-regular fa-bell" />
            </button>
            <div className="topbar-divider" />
            <div ref={avatarMenuRef} style={{ position: 'relative' }}>
              <div
                className="topbar-avatar"
                title={user?.name || 'Account'}
                onClick={() => setAvatarMenuOpen(prev => !prev)}
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" />
                ) : (
                  <div className="topbar-avatar-initials">
                    {user?.name ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'U'}
                  </div>
                )}
              </div>
              {avatarMenuOpen && (
                <div style={{
                  position: 'absolute', top: '46px', right: 0, minWidth: '220px',
                  backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-default)',
                  borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 1000, overflow: 'hidden',
                }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {user?.name || 'User'}
                      </div>
                      <span style={{
                        fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                        padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
                        backgroundColor: user?.role === 'superadmin' ? '#ef4444' : tier === 'enterprise' ? '#a855f7' : tier === 'professional' ? '#22c55e' : '#3b82f6',
                        color: '#fff',
                      }}>
                        {user?.role === 'superadmin' ? 'Admin' : tier === 'professional' ? 'Pro' : tier === 'enterprise' ? 'Enterprise' : 'Starter'}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {user?.email || ''}
                    </div>
                  </div>
                  <div style={{ padding: '4px' }}>
                    <button
                      onClick={() => { setAvatarMenuOpen(false); navigate('/account'); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                        padding: '10px 12px', border: 'none', borderRadius: '6px',
                        backgroundColor: 'transparent', color: 'var(--text-primary)',
                        fontSize: '13px', cursor: 'pointer', textAlign: 'left',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-subtle)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <UserCircle size={16} /> Account
                    </button>
                    <button
                      onClick={() => { setAvatarMenuOpen(false); logout(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                        padding: '10px 12px', border: 'none', borderRadius: '6px',
                        backgroundColor: 'transparent', color: 'var(--badge-red-text)',
                        fontSize: '13px', cursor: 'pointer', textAlign: 'left',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-subtle)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <LogOut size={16} /> Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="main-content-body">
          <AppRoutes />
        </div>
      </div>
      <ChatDrawer isOpen={apuOpen} onClose={() => setApuOpen(false)} />

      {/* Upgrade Modal */}
      {upgradeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setUpgradeModal(false)}>
          <div style={{
            backgroundColor: 'var(--bg-card)', borderRadius: '16px',
            padding: '32px', maxWidth: '420px', width: '90%',
            border: '1px solid var(--border-default)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
              Upgrade to Professional
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '20px' }}>
              This feature requires the Professional plan. Upgrade to unlock AI verification, smart locks, portfolio analytics, and the APU assistant.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <a
                href="mailto:contact@goayni.co?subject=Upgrade%20to%20Professional"
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#22c55e', color: '#fff', fontSize: '14px',
                  fontWeight: '600', textAlign: 'center', textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                Contact Us to Upgrade
              </a>
              <button
                onClick={() => setUpgradeModal(false)}
                style={{
                  padding: '12px 20px', borderRadius: '8px',
                  border: '1px solid var(--border-default)',
                  backgroundColor: 'transparent', color: 'var(--text-secondary)',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                }}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
